import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import { TrendingUp, TrendingDown, Minus, ExternalLink, Newspaper, ChevronRight } from 'lucide-react';

const IMPACT = {
  positive: { icon: TrendingUp, color: 'text-emerald-600', bg: 'bg-emerald-50' },
  negative: { icon: TrendingDown, color: 'text-red-600', bg: 'bg-red-50' },
  neutral: { icon: Minus, color: 'text-[#6B7280]', bg: 'bg-gray-50' },
};

const SRC_COLORS = {
  'NewsAPI': 'bg-blue-100 text-blue-700',
  'Federal News Network': 'bg-emerald-100 text-emerald-700',
  'GovInfo': 'bg-purple-100 text-purple-700',
};

export function NewsDigestWidget({ heldFunds }) {
  const navigate = useNavigate();
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    load();
    const interval = setInterval(load, 300000); // 5 min
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const load = async () => {
    try {
      const res = await api.get('/news/articles?limit=10');
      const all = res.data?.articles || [];
      // Filter to participant's held funds, or show all if no fund match
      const funds = (heldFunds || []).map(f => f.toLowerCase());
      let filtered = all;
      if (funds.length > 0) {
        filtered = all.filter(a =>
          (a.affectedFunds || []).some(f => funds.includes(f.toLowerCase())) ||
          (a.affectedFunds || []).length === 0 // also include general articles
        );
      }
      setArticles(filtered.slice(0, 3));
    } catch {} finally { setLoading(false); }
  };

  if (loading) {
    return (
      <div className="panel p-5" data-testid="news-digest-loading">
        <div className="h-3 bg-gray-100 rounded w-28 mb-3 animate-pulse" />
        <div className="space-y-3">
          {[0,1,2].map(i => <div key={i} className="h-10 bg-gray-50 rounded animate-pulse" />)}
        </div>
      </div>
    );
  }

  return (
    <div className="panel p-5" data-testid="news-digest-widget">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Newspaper size={13} className="text-[#6B7280]" />
          <span className="overline">News Digest</span>
        </div>
        <button
          onClick={() => navigate('/news')}
          className="text-[10px] font-medium text-[#1B2A4A] hover:underline flex items-center gap-0.5"
          data-testid="news-digest-view-all"
        >
          View All <ChevronRight size={10} />
        </button>
      </div>

      {articles.length > 0 ? (
        <div className="space-y-2.5" data-testid="news-digest-articles">
          {articles.map((a, i) => {
            const imp = IMPACT[a.impactDirection] || IMPACT.neutral;
            const ImpIcon = imp.icon;
            const srcColor = SRC_COLORS[a.source] || 'bg-gray-100 text-gray-700';
            return (
              <div key={i} className="group" data-testid={`news-digest-article-${i}`}>
                <div className="flex items-start gap-2.5">
                  <div className={`mt-1 p-1 rounded ${imp.bg} shrink-0`}>
                    <ImpIcon size={10} className={imp.color} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h4 className="text-xs font-medium text-[#1B2A4A] leading-snug line-clamp-2 group-hover:underline" data-testid="digest-headline">
                      {a.url ? (
                        <a href={a.url} target="_blank" rel="noopener noreferrer">{a.headline}</a>
                      ) : a.headline}
                    </h4>
                    <div className="flex items-center gap-1.5 mt-1 flex-wrap">
                      <span className={`text-[9px] font-medium px-1.5 py-0.5 rounded ${srcColor}`}>{a.source}</span>
                      {(a.affectedFunds || []).map(f => (
                        <span key={f} className="text-[8px] font-mono font-medium text-[#1B2A4A] bg-[#F0F1F3] px-1 py-0.5 rounded">{f}</span>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <p className="text-xs text-[#9CA3AF] text-center py-4">No relevant news. Pipeline refreshes every 15 min.</p>
      )}
    </div>
  );
}
